import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

/**
 * Completes double opt-in for signup forms via ?token= from confirmation email.
 */
const ConfirmSubscription: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [status, setStatus] = useState<'idle' | 'loading' | 'ok' | 'err'>('idle');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('err');
      setMessage('Missing confirmation token.');
      return;
    }

    let cancelled = false;
    (async () => {
      setStatus('loading');
      try {
        const fn = httpsCallable(functions, 'confirmSignupOptInPublic');
        const res = await fn({ token });
        const data = res.data as { success?: boolean; message?: string };
        if (cancelled) return;
        if (data.success) {
          setStatus('ok');
          setMessage(data.message || 'Your subscription is confirmed.');
        } else {
          setStatus('err');
          setMessage(data.message || 'Could not confirm subscription.');
        }
      } catch (e: unknown) {
        if (cancelled) return;
        setStatus('err');
        setMessage(e instanceof Error ? e.message : 'Something went wrong.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        {status === 'loading' && (
          <>
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4" />
            <p className="text-gray-700">Confirming your subscription…</p>
          </>
        )}
        {status === 'ok' && (
          <>
            <div className="text-green-500 text-5xl mb-4">✓</div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">You&apos;re all set</h1>
            <p className="text-gray-600">{message}</p>
            <Link to="/" className="inline-block mt-6 text-blue-600 hover:underline">
              Back to home
            </Link>
          </>
        )}
        {status === 'err' && (
          <>
            <div className="text-amber-500 text-5xl mb-4">!</div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Couldn&apos;t confirm</h1>
            <p className="text-gray-600">{message}</p>
            <Link to="/" className="inline-block mt-6 text-blue-600 hover:underline">
              Back to home
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default ConfirmSubscription;
